import { useRef, useState } from 'react';
import { AddressType, useFetch } from '../AddressManualEntry';
import { AddressDataMap } from './types';
import { useAddressTypeConfig } from './useAddressTypeConfig';

export const useAddressSearch = <T extends AddressType>(endpoint: string, addressType: T) => {
  const config = useAddressTypeConfig()[addressType];
  const fetchFn = useFetch();
  const [data, setData] = useState<AddressDataMap[T] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const meterNumberRef = useRef<string>('');

  const resetSearch = () => {
    setData(null);
    setError(null);
    setInputError(null);
    setIsSearching(false);
  };

  const validate = (value: string) => {
    if (meterNumberRef.current !== value) resetSearch();
    const err = config.validate(value);
    setInputError(err);
    return err;
  };

  const search = async (value: string) => {
    if (validate(value) || error) return;

    try {
      setIsSearching(true);
      meterNumberRef.current = value;

      const res = await fetchFn(config.buildUrl(endpoint, value));
      const extracted = config.extractData(res);
      if (!extracted) throw new Error();

      setData(extracted);
    } catch {
      setError(
        "Sorry, we can't find your address. Please call us on 13 24 61, so we can help you."
      );
    } finally {
      setIsSearching(false);
    }
  };

  return {
    data,
    inputError,
    error,
    isSearching,
    isConfirmEnabled: !error && !inputError && !!data,
    validate,
    search,
    resetSearch,
    config,
  };
};
